import { useEffect, useRef, useState } from "react";
import { Crown, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSelectedNation } from "@/hooks/useSelectedNation";
import { trackEvent } from "@/lib/audit";
import { VictoryCardCta } from "@/components/viral/VictoryCardCta";
import { fireVictoryPrompt } from "@/lib/viral/victoryPromptBus";

/**
 * Top-5 viral boost #5 — Nation overtake animation.
 * Polls public.nation_scores every 25s; when the user's nation moves UP
 * past another, animates a celebratory toast naming the surpassed nation.
 * Pure read, RLS-safe; no writes, no auth side-effects.
 */

interface Row { nation_code: string; total_points: number }

export function NationOvertakeToast() {
  const { nation } = useSelectedNation();
  const prevRank = useRef<number | null>(null);
  const prevList = useRef<Row[]>([]);
  const [toast, setToast] = useState<{ surpassed: string; rank: number } | null>(null);

  useEffect(() => {
    if (!nation) return;
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    async function tick() {
      const { data } = await supabase
        .from("nation_scores")
        .select("nation_code,total_points")
        .order("total_points", { ascending: false })
        .limit(60);
      if (cancelled) return;
      const list = (data ?? []) as Row[];
      const myIdx = list.findIndex((r) => r.nation_code.toUpperCase() === nation!.toUpperCase());
      if (myIdx < 0) { prevList.current = list; return; }
      const myRank = myIdx + 1;
      if (prevRank.current !== null && myRank < prevRank.current && prevList.current.length > 0) {
        // Find a nation that was above us last poll but is now below.
        const prevAbove = prevList.current
          .slice(0, prevRank.current - 1)
          .map((r) => r.nation_code.toUpperCase());
        const nowBelow = list.slice(myRank).map((r) => r.nation_code.toUpperCase());
        const surpassed = prevAbove.find((c) => nowBelow.includes(c));
        if (surpassed) {
          setToast({ surpassed, rank: myRank });
          trackEvent("nation_overtake_animated", { surpassed, rank: myRank });
          if (typeof navigator !== "undefined" && "vibrate" in navigator) {
            try { navigator.vibrate?.([40, 30, 80]); } catch { /* noop */ }
          }
          // P0-DISCOVERY-01 — non-blocking prompt instead of forced share sheet.
          setTimeout(
            () => fireVictoryPrompt({ source: "nation_overtake" }),
            1800,
          );
          setTimeout(() => { if (!cancelled) setToast(null); }, 5500);
        }
      }
      prevRank.current = myRank;
      prevList.current = list;
    }

    void tick();
    timer = setInterval(() => { void tick(); }, 25_000);
    return () => { cancelled = true; if (timer) clearInterval(timer); };
  }, [nation]);

  if (!toast) return null;

  return (
    <div className="pointer-events-none fixed top-20 left-1/2 z-[65] -translate-x-1/2 animate-slide-in-right">
      <div className="flex items-center gap-3 rounded-full border border-amber-500/60 bg-gradient-to-r from-amber-500/95 to-orange-500/95 px-4 py-2 text-sm font-black text-white shadow-2xl backdrop-blur">
        <Crown className="h-4 w-4" />
        <span>
          ¡Superaste a <span className="underline">{toast.surpassed}</span>! · #{toast.rank} mundial
        </span>
        <TrendingUp className="h-4 w-4" />
        <VictoryCardCta source="nation_overtake" compact label="Mi Card" />
      </div>
    </div>
  );
}
